// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.IntergrateStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import {
  findIndex,
  forEach,
  remove,
  flow,
  flatMap,
  getOr,
  find,
  filter,
  isNil
} from 'lodash/fp';

import {makeAbsoluteUrl} from 'share/utils/Izenda.RouteUtils';

export default class IntergrateStore {
  static partInfos: Array = [];
  static crossFilteringFilters: Array = [];

  /**
   * Catch a part info.
   * @param {Object} param
   */
  static addPart(param: Object) {
    if (!isNil(param.id))
      param.id = param.id.toLowerCase();
    const index = findIndex(info => info.id === param.id, this.partInfos);
    index === -1 && this.partInfos.push(param);
  }

  /**
   * Remove a cached part by its id.
   * @param {String} id
   */
  static removePart(id: String) {
    let idToRemove = id;
    if (!isNil(idToRemove))
      idToRemove = idToRemove.toLowerCase();
    const index = findIndex(info => info.id === idToRemove, this.partInfos);
    index !== -1 && this.partInfos.splice(index, 1);
  }

  /**
   * Add a cross filtering.
   * @param {Object} filter
   */
  static addFilter(filter: Object) {
    var index = findIndex(item => filter.key === item.key, this.crossFilteringFilters);
    if (index === -1) {
      this.crossFilteringFilters.push(filter);
    } else {
      this.crossFilteringFilters[index].value = filter.value;
    }
  }

  /**
   * Add filter collection.
   * @param {Array} filters
   */
  static addFilters(filters: Array) {
    forEach(this.addFilter.bind(this), filters);
  }

  /**
   * Check whether a part was cached.
   * @param {String} reportPartId
   */
  static isCachedCrossFilteringPart(reportPartId: String) {
    const index = findIndex(item => {
      return item.id === reportPartId &&
        item.reportPart &&
        item.reportPart.reportPartContent.isCrossFiltering;
    }, this.partInfos);
    return index !== -1;
  }

  /**
   * Clean cross filterings.
   */
  static cleanFilter() {
    this.crossFilteringFilters = remove(item => {
      let index = flow(
        flatMap(part => getOr([], 'reportPartContent.drilldown.filters', part.reportPart)),
        findIndex(filter => filter.key === item.key)
      )(this.partInfos);

      return index === -1;
    }, this.crossFilteringFilters);
  }

  /**
   * Update report part data into cached part.
   * @param {Object} reportPart
   */
  static updateReportPart(reportPart: Object) {
    if (!isNil(reportPart)) {
      let partInfo = find(item => item.id === reportPart.id, this.partInfos);
      partInfo && (partInfo.reportPart = reportPart);
    }
  }

  /**
   * Load parts for updating.
   * @param {String} reportId
   * @param {String} excludedPartId
   */
  static loadUpdatingReportParts(reportId: String, excludedPartId: String) {
    let partInfo = find(item => item.id === reportId, this.partInfos) || {};
    if (!partInfo.reportPart.reportPartContent.isCrossFiltering) {
      return [partInfo];
    }

    return filter(part => part.reportId === partInfo.reportId &&
      part.id !== excludedPartId &&
      part.reportPart.reportPartContent.isCrossFiltering,
      this.partInfos);
  }

  /**
   * Reset store.
   */
  static reset() {
    this.partIds = [];
    this.resetCrossFilteringFilters();
  }

  /**
   * Reset cross filtering's filters.
   */
  static resetCrossFilteringFilters() {
    this.crossFilteringFilters = [];
  }

  /**
   * Sync current page manifest
   */
  static syncPageManifest() {
    window.izenda = window.izenda || {};
    let { page, url } = window.izenda;
    const absUrl = makeAbsoluteUrl();

    if (url !== absUrl) {
      page = String.newGuid();
      url = absUrl;
      window.izenda.page = page;
      window.izenda.url = url;
    }

    return page;
  }
};



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.IntergrateStore.js